/**
 * v7.17 — Mercado Pago Pix via Backend Proxy.
 *
 * O Access Token MP fica APENAS no servidor (env > Mongo `app_config`).
 * O frontend chama o backend (`/api/integrations/mp/...`) que assina e
 * encaminha as requisições para api.mercadopago.com.
 *
 * MOCK: se o backend não tiver token configurado, retorna `{ mocked: true,
 * id: 'MOCK-...' }`; o polling resolve em ~4s simulando aprovação.
 */
import { loadServerConfig } from '@/lib/serverConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

let _flagsCache = null;
let _flagsCacheAt = 0;
const FLAGS_TTL_MS = 30_000;

async function _readFlags({ force = false } = {}) {
  if (!force && _flagsCache && Date.now() - _flagsCacheAt < FLAGS_TTL_MS) {
    return _flagsCache;
  }
  // v8.10 — FIX de segurança: se a config não puder ser lida (rede/cache
  // vazio), NÃO cai silenciosamente em `{}` (que virava modo MOCK com botão
  // "Simular pagamento" criando pedido real na cozinha sem cobrança).
  // Sem resposta confiável → lança e o checkout aborta com erro claro.
  let body = null;
  try {
    body = await loadServerConfig();
  } catch {
    body = null;
  }
  const flags = body?.flags;
  if (flags && typeof flags.hasMpAccessToken !== 'undefined') {
    _flagsCache = flags;
    _flagsCacheAt = Date.now();
    return flags;
  }
  if (_flagsCache) return _flagsCache;
  throw new Error('CONFIG_UNAVAILABLE');
}

export function invalidatePixCache() {
  _flagsCache = null;
  _flagsCacheAt = 0;
}

export async function isPixConfigured() {
  const flags = await _readFlags();
  return !!flags.hasMpAccessToken;
}

/**
 * Cria pagamento Pix via proxy backend. Retorna:
 *   { id, qrCodeBase64, qrCodeText, amount, status, mocked, _env }
 */
export async function createPixPayment({ amount, description, customerEmail, order, idempotencyKey }) {
  if (!API_URL) throw new Error('REACT_APP_BACKEND_URL ausente');
  // v7.78 — Timeout explícito (25s): sem ele, um fetch travado deixava o
  // botão preso em "Gerando pagamento..." indefinidamente.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 25000);
  try {
    const resp = await fetch(`${API_URL}/api/integrations/mp/create-pix`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount, description, customerEmail, order, idempotencyKey }),
      signal: controller.signal,
    });
    if (!resp.ok) {
      const txt = await resp.text().catch(() => '');
      if (resp.status === 423) {
        // v7.98 — delivery pausado pelo admin (gate server-side)
        let msg = 'Restaurante temporariamente fechado para novos pedidos.';
        try { msg = JSON.parse(txt)?.detail || msg; } catch {}
        throw new Error(msg);
      }
      if (resp.status === 409) {
        let msg = 'Já existe um pagamento idêntico em andamento. Aguarde alguns segundos.';
        try { msg = JSON.parse(txt)?.detail || msg; } catch {}
        throw new Error(msg);
      }
      // v8.10 — não expõe o payload bruto do MP ao cliente; técnico vai pro console.
      // eslint-disable-next-line no-console
      console.error(`[MP PROXY] create-pix HTTP ${resp.status}:`, txt.slice(0, 300));
      throw new Error('Não foi possível gerar o Pix agora. Tente novamente em instantes.');
    }
    return await resp.json();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[MP PROXY] createPixPayment falhou:', err);
    if (err?.name === 'AbortError') {
      throw new Error('O servidor de pagamento demorou demais para responder. Verifique sua conexão e tente novamente.');
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * v8.10 — Polling da FONTE DE VERDADE server-side (`/api/orders/payment-state`).
 * O status só sai de `awaiting_payment` quando o WEBHOOK do Mercado Pago (ou
 * o reconciler) confirmar `approved` no servidor — o cliente nunca mais
 * decide sozinho que o pagamento foi feito. MOCK- é promovido pelo próprio
 * servidor (preview sem token). Resolve com 'approved' ou rejeita
 * (timeout, expired, cancelled).
 */
export async function pollPaymentStatus(paymentId, { onTick, intervalMs = 5000, timeoutMs = 300000 } = {}) {
  const isMock = String(paymentId).startsWith('MOCK-');
  const deadline = Date.now() + timeoutMs;
  let attempt = 0;
  let notFoundStreak = 0;
  while (Date.now() < deadline) {
    attempt += 1;
    try {
      const resp = await fetch(`${API_URL}/api/orders/payment-state/${encodeURIComponent(paymentId)}`);
      if (resp.ok) {
        const data = await resp.json();
        const state = data.state;
        // v8.10 — fail-fast: cobrança sem doc no servidor nunca será aprovada.
        if (state === 'not_found') {
          notFoundStreak += 1;
          if (notFoundStreak >= 6) {
            throw new Error('Cobrança não localizada no servidor. Volte e gere um novo Pix.');
          }
        } else {
          notFoundStreak = 0;
        }
        onTick?.({ status: data.approved ? 'approved' : 'pending', attempt, mocked: isMock });
        if (data.approved) {
          return { status: 'approved', paidAt: data.paidAt || new Date().toISOString(), raw: data };
        }
        if (state === 'expired') {
          throw new Error('Pagamento expirado (30 min sem confirmação). O pedido NÃO foi enviado à cozinha — gere um novo Pix.');
        }
        if (state === 'cancelled') {
          throw new Error('Pagamento cancelado. O pedido não foi enviado à cozinha.');
        }
      } else {
        // eslint-disable-next-line no-console
        console.warn('[MP PROXY] poll HTTP', resp.status);
      }
    } catch (err) {
      if (/expirado|cancelado|não localizada/i.test(err?.message || '')) throw err;
      // eslint-disable-next-line no-console
      console.warn('[MP PROXY] poll erro (vai retry):', err?.message || err);
    }
    await new Promise((r) => setTimeout(r, isMock ? 1500 : intervalMs));
  }
  throw new Error('timeout');
}

export const MercadoPagoAPI = {
  isConfigured: isPixConfigured,
  createPix: createPixPayment,
  poll: pollPaymentStatus,
};
